import { useLazyQuery, useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Collapse,
  Form,
  FormGroup,
  Input,
  Label,
  ButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { EMPTY_COVER } from '../../assets/Images';
import {
  CREATE_CHANNEL,
  DELETE_ALL_CHANNEL_MESSAGES,
  DELETE_CHANNEL,
  DELETE_CHANNEL_MESSAGE,
  EDIT_AI_CHANNEL_MESSAGE_TEXT,
  UPDATE_CHANNEL,
} from '../../mutation/mutation';
import ChannelMessageCreator from './ChannelMessageCreator';
import BackgroundImage from './backgroundImage';
import templatesFonts from '../../fonts/templates-fonts';
import { fontIsDeprecated } from '../templates-manager/utils';
import { GET_CHANNEL_PREVIEWS } from '../../query/query';

const ChannelEditor = (props) => {
  const {
    isCreation,
    channel,
    toggleModal,
    toggleModalSuccess,
    toggleModalError,
    opened,
  } = props;

  const [getChannelPreviews, {
    data: { savGetChannelPreviews = [] } = {}, loading,
  }] = useLazyQuery(GET_CHANNEL_PREVIEWS, {
    fetchPolicy: 'cache-and-network',
    variables: { channelId: channel ? channel._id : '' },
  }) || {};

  const [coverImage, setCoverImage] = useState(channel.picture);
  const [defaultBackground, setDefaultBackground] = useState(channel.defaultBackground);
  const [isNewDefaultBackground, setIsNewDefaultBackground] = useState(false);
  const [isNewImage, setIsNewImage] = useState(false);

  const [formData, setFormData] = useState({
    ...channel,
    generatedMessageFontSize: channel.generatedMessageFontSize || 72,
    generatedMessageColor: channel.generatedMessageColor || '#ffffff',
    generatedMessageMarginRight: channel.generatedMessageMarginRight || 50,
    generatedMessageMarginBottom: channel.generatedMessageMarginBottom || 0,
    generatedMessageMarginTop: channel.generatedMessageMarginTop || 0,
    generateNumber: 0,
    generatedMessageMarginLeft: channel.generatedMessageMarginLeft || 50,
    generatedMessageFontFamily: channel.generatedMessageFontFamily || 'Raleway-Regular',
    regenerateContent: channel._id ? false : undefined,
    previews: savGetChannelPreviews,
  });

  const [isOpen, setIsOpen] = useState(opened);
  const [isOpen2, setIsOpen2] = useState(false);

  const [updateChannel] = useMutation(UPDATE_CHANNEL);
  const [deleteChannel] = useMutation(DELETE_CHANNEL);
  const [deleteAllChannelMessages] = useMutation(DELETE_ALL_CHANNEL_MESSAGES);
  const [createChannel] = useMutation(CREATE_CHANNEL);
  const [deleteChannelMessage] = useMutation(DELETE_CHANNEL_MESSAGE);
  const [editAIChannelMessageText] = useMutation(EDIT_AI_CHANNEL_MESSAGE_TEXT);

  const onSetCoverImage = (img) => {
    setIsNewImage(true);
    setCoverImage(img);
  };

  const onSetDefaultBackground = (img) => {
    setIsNewDefaultBackground(true);
    setDefaultBackground(img);
  };

  const toggleCollapse = () => {
    setIsOpen(!isOpen);
  };

  const toggleCollapse2 = () => {
    setIsOpen2(!isOpen2);
  };

  const [deletingChannel, setDeletingChannel] = useState(false);
  const onDeleteChannel = async () => {
    // eslint-disable-next-line no-alert
    if (window.confirm('⚠️⚠️⚠️ Are you sure you want to delete this channel and all the channel messages? It cannot be undone!')) {
      setDeletingChannel(true);
      await deleteChannel({
        variables: {
          channelId: channel._id,
        },
      }).then(() => { window.location.reload(); })
        .catch((err) => { toggleModalError(err.message); });
      setDeletingChannel(false);
    }
  };
  const [deletingAllChannelMessages, setDeletingAllChannelMessages] = useState(false);
  const onDeleteAllChannelMessages = async () => {
    // eslint-disable-next-line no-alert
    if (window.confirm('⚠️⚠️⚠️ Are you sure you want to delete all the channel messages? It cannot be undone!')) {
      setDeletingAllChannelMessages(true);
      await deleteAllChannelMessages({
        variables: {
          channelId: channel._id,
        },
      }).then(() => { window.location.reload(); })
        .catch((err) => { toggleModalError(err.message); });
      setDeletingAllChannelMessages(false);
    }
  };

  const onSubmitChannel = () => {
    toggleModal(true);
    const updatedChannel = {
      nameFr: formData.nameFr,
      nameEn: formData.nameEn,
      description: formData.description,
      AIPrompt: formData.AIPrompt,
      premium: formData.premium,
      hidden: formData.hidden,
      spicy: formData.spicy,
      picture: isNewImage ? coverImage : undefined,
      defaultBackground: isNewDefaultBackground ? defaultBackground : undefined,
      generatedMessageFontSize: parseInt(formData.generatedMessageFontSize, 10),
      generatedMessageColor: formData.generatedMessageColor,
      generatedMessageMarginRight: parseInt(formData.generatedMessageMarginRight, 10),
      generatedMessageMarginLeft: parseInt(formData.generatedMessageMarginLeft, 10),
      generatedMessageMarginBottom: parseInt(formData.generatedMessageMarginBottom, 10),
      generatedMessageMarginTop: parseInt(formData.generatedMessageMarginTop, 10),
      generatedMessageFontFamily: formData.generatedMessageFontFamily,
      regenerateContent: channel._id ? formData.regenerateContent : undefined,
      generateNumber: parseInt(formData.generateNumber, 10),
    };

    if (isCreation) {
      createChannel({
        variables: {
          channel: updatedChannel,
        },
      }).then(() => { toggleModalSuccess(true); })
        .catch((err) => { toggleModalError(err.message); });
    } else {
      updateChannel({
        variables: {
          channelId: channel._id,
          updatedChannel,
        },
      }).then(() => { toggleModalSuccess(true); })
        .catch((err) => { toggleModalError(err.message); });
    }
  };

  const [deletedMessageIds, setDeletedMessageIds] = useState([]);

  const onDeleteChannelMessage = async (channelMessageId) => {
    await deleteChannelMessage({
      variables: {
        channelMessageId,
      },
    }).then(() => {
      // TODO we should use Apollo Cache instead, modifying SavGetChannels
      // This is bad but working...
      setDeletedMessageIds([...deletedMessageIds, channelMessageId]);
    })
      .catch((err) => { toggleModalError(err.message); });
  };

  const [editTextLoadings, setEditTextLoadings] = useState({});
  const [editedText, setEditedText] = useState({});

  const onEditAIChannelMessageText = async (channelMessageId, text) => {
    const updatedTextLoadings = { ...editTextLoadings };
    updatedTextLoadings[channelMessageId] = true;
    setEditTextLoadings(updatedTextLoadings);
    const result = await editAIChannelMessageText({
      variables: {
        channelMessageId,
        text,
      },
    });
    const newUrl = result.data.savEditAIChannelMessageText.thumbnailEn;
    // TODO we should use Apollo Cache instead, modifying SavGetChannels
    // This is bad but working...
    setFormData({
      ...formData,
      previews: formData.previews.map((p) => {
        if (p._id !== channelMessageId) return p;
        return { ...p, thumbnailEn: newUrl, thumbnailFr: newUrl };
      }),
    });
    const updatedTextLoadings2 = { ...editTextLoadings };
    updatedTextLoadings2[channelMessageId] = false;
    setEditTextLoadings(updatedTextLoadings2);
    const updatedEditedText = { ...editedText };
    updatedEditedText[channelMessageId] = false;
    setEditedText(updatedEditedText);
  };

  const imageCoverSize = 70;

  const [showImages, setShowImages] = useState(false);
  useEffect(() => {
    (async () => {
      if (showImages) {
        const res = await getChannelPreviews();
        setFormData({
          ...formData,
          previews: res.data.savGetChannelPreviews,
        });
      }
    })();
  }, [showImages]);
  const [showTextOnly, setShowTextOnly] = useState(false);
  const [fontFamilyIsOpen, setFontFamilyIsOpen] = useState(false);
  const toggleFontFamilyOpen = () => setFontFamilyIsOpen(!fontFamilyIsOpen);

  return (
    <Card style={{ margin: 10 }}>
      <CardHeader onClick={toggleCollapse}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h4>
            {channel.countChannelMessages.neverSent === 0 && (!(channel.nameEn || channel.nameFr).startsWith('30'))
            && (
              '❌ '
            )}
            {channel.countChannelMessages.neverSent !== 0
            && channel.countChannelMessages.neverSent < 7
            && !((channel.nameEn || channel.nameFr).startsWith('30'))
            && (
              '⚠️ '
            )}
            {channel.nameFr ? channel.nameFr : channel.nameEn}
            {channel._id && ` (${channel.countChannelMessages.neverSent} / ${channel.countChannelMessages.total})`}
          </h4>
          {channel.AIPromptStatus && (
          <span>
            {channel.AIPromptStatus}
          </span>
          )}
          <span>{isOpen ? '-' : '+'}</span>
        </div>
      </CardHeader>
      <Collapse isOpen={isOpen}>
        <CardBody>
          <Form>
            <BackgroundImage
              title="Channel Profile Picture"
              setImage={onSetCoverImage}
              width={200}
              height={200}
            />
            <img
              style={{
                margin: 20,
                width: imageCoverSize,
                height: imageCoverSize,
                borderRadius: imageCoverSize,
              }}
              src={coverImage === undefined ? EMPTY_COVER : coverImage}
              alt=""
            />
            <FormGroup>
              <Label for="channelNameFr">
                Name (Fr)
              </Label>
              <Input
                id="channelNameFr"
                name="nameFr"
                value={formData.nameFr}
                onChange={(e) => setFormData({ ...formData, nameFr: e.target.value })}
                placeholder="Nom du channel (FR) - laisser vide pour que les utilisateurs FR ne voient pas ce channel"
              />
            </FormGroup>
            <FormGroup>
              <Label for="channelNameEn">
                Name (En)
              </Label>
              <Input
                id="channelNameEn"
                name="nameEn"
                value={formData.nameEn}
                onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                placeholder="Nom du channel (En) - keep empty if you don't want EN users to see this channel"
              />
            </FormGroup>
            <FormGroup
              check
              inline
            >
              <Input type="checkbox" id="channelPremium" checked={formData.premium} onChange={(e) => setFormData({ ...formData, premium: e.target.checked })} />
              <Label check>
                Channel is premium
              </Label>
            </FormGroup>
            <FormGroup
              check
              inline
            >
              <Input type="checkbox" id="channelHidden" checked={formData.hidden} onChange={(e) => setFormData({ ...formData, hidden: e.target.checked })} />
              <Label check>
                Channel is hidden
              </Label>
            </FormGroup>
            <FormGroup
              check
              inline
            >
              <Input type="checkbox" id="channelSpicy" checked={formData.spicy} onChange={(e) => setFormData({ ...formData, spicy: e.target.checked })} />
              <Label check>
                Channel is spicy
              </Label>
            </FormGroup>
            <FormGroup>
              <Label for="channelDescription">
                Description
              </Label>
              <Input
                id="channelDescription"
                name="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description du channel"
              />
            </FormGroup>
            <FormGroup>
              <Label for="AIPrompt">
                OpenAI Prompt
              </Label>
              <Input
                style={{ maxWidth: 800 }}
                id="AIPrompt"
                type="textarea"
                rows={formData.AIPrompt ? '5' : '1'}
                name="AIPrompt"
                value={formData.AIPrompt}
                onChange={(e) => setFormData({ ...formData, AIPrompt: e.target.value })}
                placeholder="Open AI Prompt (leave blank unless you want content to be generated by OpenAI)"
              />
            </FormGroup>
            <div style={{ display: formData.AIPrompt ? 'block' : 'none' }}>
              <FormGroup style={{ display: 'inline-block' }}>
                <Label for="fontFamily"># of pieces of content to generate</Label>
                <Input type="number" name="fontSize" min={1} value={formData.generateNumber} onChange={(e) => setFormData({ ...formData, generateNumber: e.target.value })} />
              </FormGroup>
              <FormGroup
                check
                inline
                style={{ marginLeft: 20 }}
              >
                <Input type="checkbox" id="regenerateContent" checked={formData.regenerateContent} onChange={(e) => setFormData({ ...formData, regenerateContent: e.target.checked })} />
                <Label check>
                  Swipe existing content
                </Label>
              </FormGroup>
            </div>
            <div style={{ display: formData.AIPrompt ? 'block' : 'none' }}>
              <BackgroundImage
                title="Default background (used for Open AI Prompt channels only)"
                setImage={onSetDefaultBackground}
              />
              <FormGroup>
                <Label for="fontFamily">font-size</Label>
                <Input type="number" name="fontSize" min={1} value={formData.generatedMessageFontSize} onChange={(e) => setFormData({ ...formData, generatedMessageFontSize: e.target.value })} />
              </FormGroup>
              <FormGroup>
                <Label for="x">color</Label>
                <Input type="color" name="color" value={formData.generatedMessageColor} onChange={(e) => setFormData({ ...formData, generatedMessageColor: e.target.value })} />
              </FormGroup>
              <FormGroup>
                <Label for="fontFamily">Margin Right</Label>
                <Input type="number" name="marginright" min={0} value={formData.generatedMessageMarginRight} onChange={(e) => setFormData({ ...formData, generatedMessageMarginRight: e.target.value })} />
              </FormGroup>
              <FormGroup>
                <Label for="fontFamily">Margin Left</Label>
                <Input type="number" name="marginleft" min={0} value={formData.generatedMessageMarginLeft} onChange={(e) => setFormData({ ...formData, generatedMessageMarginLeft: e.target.value })} />
              </FormGroup>
              <FormGroup>
                <Label for="fontFamily">Margin Top</Label>
                <Input type="number" name="margintop" min={0} value={formData.generatedMessageMarginTop} onChange={(e) => setFormData({ ...formData, generatedMessageMarginTop: e.target.value })} />
              </FormGroup>
              <FormGroup>
                <Label for="fontFamily">Margin Bottom</Label>
                <Input type="number" name="marginbottom" min={0} value={formData.generatedMessageMarginBottom} onChange={(e) => setFormData({ ...formData, generatedMessageMarginBottom: e.target.value })} />
              </FormGroup>
              <FormGroup>
                <Label for="fontFamily">font-family</Label>
                <ButtonDropdown isOpen={fontFamilyIsOpen} toggle={() => toggleFontFamilyOpen()}>
                  <DropdownToggle color="info" style={{ fontFamily: formData.generatedMessageFontFamily, marginLeft: 15 }} caret>
                    {formData.generatedMessageFontFamily}
                  </DropdownToggle>
                  <DropdownMenu style={{ height: 300, overflowY: 'scroll' }}>
                    {templatesFonts
                      .filter(({ name }) => !fontIsDeprecated(name))
                      .map(({ fontFamily, name: fontName }) => (
                        <DropdownItem
                          key={fontName}
                          onClick={
                            () => setFormData(
                              { ...formData, generatedMessageFontFamily: fontFamily },
                            )
                          }
                          style={{ fontFamily }}
                        >
                          {fontName}
                        </DropdownItem>
                      ))}
                  </DropdownMenu>
                </ButtonDropdown>
              </FormGroup>
            </div>
            <FormGroup>
              {!isCreation && (
              <div>
                <Button color="primary" onClick={() => setShowImages(!showImages)} style={{ marginTop: 20, marginBottom: 20 }}>
                  {showImages ? 'Hide Channel Content' : 'Show Channel Content'}
                </Button>
                <Button disabled={deletingAllChannelMessages} color="danger" onClick={onDeleteAllChannelMessages} style={{ marginTop: 20, marginBottom: 20, marginLeft: 10 }}>
                  Delete all channel messages
                </Button>
                <div style={{ display: (formData.AIPrompt && showImages) ? 'block' : 'none' }}>
                  <FormGroup
                    check
                    inline
                    style={{ marginLeft: 20 }}
                  >
                    <Input type="checkbox" id="showtextonly" checked={showTextOnly} onChange={(e) => setShowTextOnly(e.target.checked)} />
                    <Label check>
                      Show text only
                    </Label>
                  </FormGroup>
                </div>
                {loading && (
                  <div style={{ marginTop: 5, marginBottom: 5 }}>
                    Loading channel messages...
                  </div>
                )}
                {showImages && savGetChannelPreviews && savGetChannelPreviews.length > 0
                && formData.previews && formData.previews.length > 0 && !loading && (
                <div>
                  {savGetChannelPreviews.map((p, index) => (
                    !deletedMessageIds.includes(p._id) && (
                      <div key={p.thumbnailEn}>
                        {p.AIGeneratedText && (
                          <div>
                            <p style={{ fontSize: 20, fontWeight: 'bold' }}>{p.AIGeneratedTextSubCategory}</p>
                          </div>
                        )}
                        <Button disabled={deletingChannel} color="danger" outline onClick={() => onDeleteChannelMessage(p._id)} style={{ marginTop: 20 }}>Delete</Button>
                        <Button disabled={editTextLoadings[p._id] || !editedText[p._id]} color="primary" outline onClick={() => onEditAIChannelMessageText(p._id, formData.previews[index].AIGeneratedText)} style={{ marginTop: 20, marginLeft: 20 }}>{editTextLoadings[p._id] === true ? 'Saving...' : 'Save text'}</Button>
                        {p.AIGeneratedText && (
                          <div style={{ marginTop: 10 }}>
                            <Input
                              style={{
                                fontSize: 20, maxWidth: 500, display: 'inline-block', verticalAlign: 'top', marginRight: 20,
                              }}
                              id="AIPrompt"
                              type="textarea"
                              name="AIPrompt"
                              rows="5"
                              onChange={(e) => {
                                setFormData(
                                  {
                                    ...formData,
                                    previews: formData.previews.map(
                                      (value, i) => (
                                        i === index
                                          ? { ...value, AIGeneratedText: e.target.value }
                                          : value
                                      ),
                                    ),
                                  },
                                );
                                const updatedEditedText = { ...editedText };
                                updatedEditedText[p._id] = true;
                                setEditedText(updatedEditedText);
                              }}
                              defaultValue={formData.previews[index].AIGeneratedText}
                              placeholder="Open AI Prompt (leave blank unless you want content to be generated by OpenAI)"
                            />
                            {!showTextOnly && (
                            <img
                              style={{
                                display: 'inline-block',
                                verticalAlign: 'top',
                                borderWidth: '5px',
                                borderStyle: 'solid',
                                borderColor: new Date(formData.previews[index].lastSent) > 0 ? 'red' : 'transparent',
                              }}
                              src={formData.previews[index].thumbnailEn}
                              className="img-fluid"
                              alt="channel preview"
                              title={formData.previews[index].lastSent.toString()}
                            />
                            )}
                          </div>
                        )}
                        <div style={{
                          flexDirection: 'horizontal', margin: 10, width: '100%', justifyContent: 'center', display: 'flex',
                        }}
                        />
                        {!p.AIGeneratedText && (
                          <div>
                            <img
                              src={formData.previews[index].thumbnailEn}
                              className="img-fluid"
                              alt="channel preview"
                              title={formData.previews[index].lastSent.toString()}
                              style={{
                                borderWidth: '5px',
                                borderStyle: 'solid',
                                borderColor: new Date(formData.previews[index].lastSent) > 0 ? 'red' : 'transparent',
                              }}
                            />
                            <img
                              src={formData.previews[index].thumbnailFr}
                              className="img-fluid"
                              alt="channel preview"
                              title={formData.previews[index].lastSent.toString()}
                              style={{
                                borderWidth: '5px',
                                borderStyle: 'solid',
                                borderColor: new Date(formData.previews[index].lastSent) > 0 ? 'red' : 'transparent',
                              }}
                            />
                          </div>
                        )}
                        <hr />
                      </div>
                    )
                  ))}
                </div>
                )}
                <CardHeader onClick={toggleCollapse2}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h4>Add channel images</h4>
                    <span>{isOpen2 ? '-' : '+'}</span>
                  </div>
                </CardHeader>
                <Collapse isOpen={isOpen2}>
                  <CardBody>
                    <ChannelMessageCreator channelId={channel._id} />
                  </CardBody>
                </Collapse>
              </div>
              )}
            </FormGroup>
            <Button color="primary" onClick={() => onSubmitChannel()}>
              Submit
            </Button>
            <Button color="danger" onClick={() => onDeleteChannel()} style={{ marginLeft: 200 }}>
              Delete channel permanently
            </Button>
          </Form>
        </CardBody>
      </Collapse>
    </Card>
  );
};

export default ChannelEditor;



// WEBPACK FOOTER //
// ./src/components/channels/ChannelEditor.js